import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Alert } from "@material-ui/lab";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import CustomTable from "./Table1";
import { SDCloudBackendAPIWithToken } from "../../../apis/backendAPI";
import LoaderHoc from "../../../components/HOC/LoaderHoc";
import RightDrawer from "../components/RightDrawer";

import { TextField, Button } from "@material-ui/core";

const EditableQuota = React.memo(function EditableQuota({
  rowKey,
  initialValue,
  onCommit, 
  originalRow,
}) {
  const [value, setValue] = useState(initialValue ?? "");

  useEffect(() => {
    if (initialValue !== undefined && initialValue !== value) {
      setValue(initialValue ?? "");
    }
  }, [initialValue]);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    onCommit(rowKey, v, originalRow);
  };

  return (
    <TextField
      type="number"
      variant="filled"
      fullWidth
      value={value}
      onChange={handleChange}
    />
  );
});

const UserProfileData = (props) => {
  
  const description = props.description;
  const training_name = props.training_name;

  const filterRef = useRef(null);

  // States
  const [rows, setRows] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentScreen, setCurrentScreen] = useState();
  const [currentProfile, setCurrentProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [learningPerms, setLearningPerms] = useState(null);
  const [sandboxPerms, setSandboxPerms] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [hoursState, setHoursState] = useState({});
  const [quotaErrors, setQuotaErrors] = useState({});
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  const inputProps = {
    disableAnimation: true,
    shrink: false,
  };

  const hasLearning = rows.some((item) => item.resource_type === "Learning");

  useEffect(() => {
    const activeErrors = Object.keys(quotaErrors);
    setDismissedAlerts((prev) => {
      const updated = { ...prev };
      activeErrors.forEach((key) => {
        // If there's a new error, reset dismissed state
        if (quotaErrors[key] && prev[key]) {
          updated[key] = false;
        }
      });
      return updated;
    });
  }, [quotaErrors]);
    
  // useEffects
  useEffect(() => {
    renderData();
  }, []);

  const handleQuotaCommit = useCallback(
    (key, val, originalRow) => {
      setHoursState((prev) => ({
        ...prev,
        [key]: { quota: val },
      }));
      // run validation
      checkQuotaRange(
        { target: { value: val } }, 
        originalRow.skuname,
        originalRow.allocated_hours,
        originalRow.lab_hours,
        originalRow.username,
        originalRow.allocated_quota
      );
    },
    []
  );
  const columns = React.useMemo(
    () => [
      {
        Header: "Username",
        accessor: "username",
      },
      {
        Header: "SKU Name",
        accessor: "skuname",
      },
      ...(hasLearning
        ? [
            {
              Header: "Lab Hours",
              accessor: "lab_hours",
            },
          ]
        : []),
      {
        Header: "Consumed Hours",
        accessor: "hours_consumed",
      },
      {
        Header: "Allocated Quota",
        accessor: "allocated_quota",
        Cell: ({ row }) => {
          const user = row.original.username;
          const sku = row.original.skuname;
          const key = `${user}_${sku}`;
          const rawValue = hoursState[key]?.quota ?? row.original.allocated_quota ?? "";

          const initial =
            rawValue === "" || rawValue == null
              ? ""
              : Math.round(Number(rawValue));
          return (
            <EditableQuota
              rowKey={key}
              initialValue={initial}
              onCommit={handleQuotaCommit}
              originalRow={row.original}
            />
          );
        },
      },
      {
        Header: "Available Quota",
        accessor: "available_quota",
      },
    ],
    [hasLearning, handleQuotaCommit]
  );

  const checkQuotaRange = (e, resourceName,currentAllocated, lab_hours, username, allocated_quota) => {
    const rawValue = e.target.value;
    const quota = Number(rawValue);
    let error = "";
    if (!quota && quota !== 0) {
      error = "";
    } else if (rawValue && rawValue.includes(".")) {
      error = `Decimal values are not allowed for ${resourceName}. Please enter a whole number.`;
    } else if (quota < Number(allocated_quota)) {
      error = `Additional Quota for ${resourceName} cannot be less than the existing value of ${allocated_quota} for user ${username}`;
    } else if (quota > Number(currentAllocated)) {
      error = `Additional Quota for ${resourceName} cannot exceed allocated hours ${currentAllocated} for user ${username}.`;
    } else if (lab_hours) {
      const min = lab_hours;
      if (quota < min + allocated_quota) {
        error = `Additional Quota for ${resourceName} should be at least equal to lab hours for Learnings and Showcases for user ${username}`;
      } else if (quota > min + allocated_quota) {
        error = `Additional Quota for ${resourceName} cannot exceed lab hours for Learnings and Showcases for user ${username}`;
      }
    } else {
      if (quota < 0) {
        error = `Additional Quota for ${resourceName} cannot be less than 0 for Sandboxes for user ${username}`;
      } else if (quota > allocated_quota + 12) {
        error = `Additional Quota for ${resourceName} cannot be more than ${allocated_quota + 12} for Sandboxes for user ${username}`;
      }
    }
    setNotificationType("error");
    setNotificationMessage(error);
    const uniqueKey = `${username}_${resourceName}`;

    setQuotaErrors((prev) => ({
      ...prev,
      [uniqueKey]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      permissions: rows.map(row => ({
        username: row.username,
        profile_name: row.profile_name,
        resource_type: row.resource_type,
        resource_name: row.skuname,
        permission: "create",
        allocated_hours: row.allocated_hours,
        allocated_quota: hoursState[`${row.username}_${row.skuname}`]?.quota,
      }))
    };
    SDCloudBackendAPIWithToken()
      .post("instructer/update_instructer_profile_details", payload)
      .then((resp) => resp.data)
      .then((data) => {
        renderData(); 
        setShowNotification(true);
        setNotificationType("success");
        setNotificationMessage("Profile updated successfully");
        if (filterRef.current) {
          filterRef.current(); 
        }
      })
      .catch((err) => {
        let supportInfo = (
          <p>
            {err.response.data.message}. Visit our{" "}
            <a href="https://criterion.zendesk.com/hc/en-us" target="_blank">
              help center
            </a>{" "}
            for resources and support.
          </p>
        );
        setShowNotification(true);
        setNotificationType("error");
        setNotificationMessage(supportInfo);
      });
  }

  const renderData = async () => {
    SDCloudBackendAPIWithToken()
      .post("instructer/instructer_user_details", { reference_id: props.reference_id })
      .then((resp) => resp.data.user_training_details)
      .then((resp) => {

        const newRows = resp.map((item) => ({
          username: item.username,
          profile_name: item.profile_name,
          skuname: item.sku_name,
          allocated_hours: parseInt(item.allocated_hours),
          hours_consumed: item.consumed_hours ? parseFloat(item.consumed_hours).toFixed(2) : 0 ,
          available_quota: item.available_quota ? parseFloat(item.available_quota).toFixed(2) : 0 ,
          resource_type: item.resource_type,
          lab_hours: item.lab_hours,
          allocated_quota: item.allocated_quota,
        }));

        setRows(newRows);

        // Initialize hoursState from the backend data
        const initialHours = {};
        newRows.forEach((row) => {
          const key = `${row.username}_${row.skuname}`;
          initialHours[key] =
            row.allocated_quota === "" || row.allocated_quota === null || row.allocated_quota === undefined
              ? { quota: "" }
              : { quota: Number(row.allocated_quota) };
        });

        setHoursState(initialHours);

        setIsLoading(false);
      })
      .catch((err) => {
        const message = err?.response?.data?.message || err?.message || "Failed to retrieve the data";
        setShowNotification(true);
        setNotificationType("error");
        setNotificationMessage(message);
        setIsLoading(false);
      });
  };
  const tableData = useMemo(() => rows, [rows]);


  return (
    <div>
      <form onSubmit={handleSubmit}>
      <Grid container>
        <Grid item xs={12} md={12}>
          {showNotification ? (
            <Alert
              onClose={() => setShowNotification(false)}
              severity={notificationType}
            >
              {notificationMessage}
            </Alert>
          ) : null}
        </Grid>
        <Grid item xs={12} md={12}>
          <LoaderHoc isLoading={isLoading}>
            <CustomTable
              columns={columns}
              data={tableData}
              tableTitle="Profile Details"
              dropDownValues={[]}
              onFilterRef={filterRef}
            />
          </LoaderHoc>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="outlined"
            color="secondary"
            disableElevation
            size="large"
            fullWidth
            disableFocusRipple
            type="submit"
            disabled={Object.values(quotaErrors).some(Boolean)}
          >
            Submit
          </Button>
        </Grid>
        <Grid item xs={12} md={12}>
          {Object.entries(quotaErrors).map(([resource, message]) => {
            if (!message || dismissedAlerts[resource]) return null; // hide if dismissed
            return (
              <Alert
                key={resource}
                severity="error"
                onClose={() =>
                  setDismissedAlerts((prev) => ({ ...prev, [resource]: true }))
                }
                sx={{ mb: 1 }}
              >
                {message}
              </Alert>
            );
          })}
        </Grid>
      </Grid>
      </form>
    </div>
  );
};

export default UserProfileData;